import { type ItemOf, type PageRect } from '@shared/board'
import { useId } from 'react'
import { CLIP_CAPTION_H } from '@shared/material'
import { clsx } from 'clsx'
import { BookOpen } from 'lucide-react'
import { useI18n } from '@/i18n/i18n'
import { useMaterial, useRegionCanvas } from '@/materials/hooks'
import { ClipText } from '@/materials/ClipText'
import { DRAG_HANDLE, NO_DRAG } from '../classes'
import { useBoardCtx } from '../context'

/**
 * Mutes what a clip shows around what it points at: the viewport stays
 * readable for context, the focus stands out. Shared with every place a clip
 * is drawn (board, embeds, scenes).
 */
export function FocusMask({ view, focus }: { view: PageRect; focus: PageRect[] }) {
  const id = useId().replace(/[^A-Za-z0-9_-]/g, '')
  // Viewport-relative boxes, in a 0..1 square stretched over the clip.
  const rel = focus.map((f) => ({
    x: (f.x - view.x) / view.w,
    y: (f.y - view.y) / view.h,
    w: f.w / view.w,
    h: f.h / view.h,
  }))
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 size-full"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
    >
      <defs>
        <mask id={`focus-${id}`}>
          <rect x="0" y="0" width="1" height="1" fill="white" />
          {rel.map((r, i) => (
            <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} fill="black" />
          ))}
        </mask>
      </defs>
      <rect
        x="0"
        y="0"
        width="1"
        height="1"
        fill="#fff"
        fillOpacity="0.62"
        mask={`url(#focus-${id})`}
      />
      {rel.map((r, i) => (
        <rect
          key={i}
          x={r.x}
          y={r.y}
          width={r.w}
          height={r.h}
          fill="var(--ai)"
          fillOpacity="0.08"
        />
      ))}
    </svg>
  )
}

/**
 * Part of a material's page on the board, drawn from the original PDF (only
 * which page and which region are kept). The caption says where it is from
 * and opens the reader there.
 */
export function ClipView({ item, selected }: { item: ItemOf<'clip'>; selected: boolean }) {
  const { t } = useI18n()
  const { reader } = useBoardCtx()
  const { materialId, page, rect } = item.data
  const material = useMaterial(materialId)
  const canvas = useRegionCanvas(material, page, rect, item.w)
  const p = material?.pages[page - 1]
  const aspect = p ? (rect.h * p.h) / (rect.w * p.w) : (item.h - CLIP_CAPTION_H) / item.w
  const open = () => {
    reader.open(materialId, page, rect)
  }
  return (
    <div
      className={clsx(
        'moot-node relative cursor-grab overflow-hidden rounded-md border bg-surface shadow-sm active:cursor-grabbing',
        DRAG_HANDLE,
        selected ? 'border-(--focus)' : 'border-line',
      )}
      style={{ width: item.w }}
      onDoubleClick={(e) => {
        // Double-clicking words selects them; elsewhere it opens the page.
        if (!(e.target as Element).closest('.textLayer')) open()
      }}
    >
      <div className="relative bg-white" style={{ height: Math.round(item.w * aspect) }}>
        <canvas
          ref={canvas}
          aria-label={item.data.text ?? t('clip.figure')}
          className="block size-full"
        />
        {item.data.focus && <FocusMask view={rect} focus={item.data.focus.rects} />}
        {/* Selected, its words can be selected (it is moved by its caption then). */}
        {selected && material && (
          <ClipText
            item={item}
            material={material}
            width={item.w}
            height={Math.round(item.w * aspect)}
          />
        )}
      </div>
      <div
        className="flex items-center gap-1.5 border-t border-line px-2 text-[11.5px] text-muted"
        style={{ height: CLIP_CAPTION_H }}
      >
        <span className="min-w-0 flex-1 truncate">
          {material ? material.name : t('clip.missing')}
        </span>
        <button
          type="button"
          onClick={open}
          title={t('clip.open')}
          className={
            NO_DRAG +
            ' inline-flex flex-none items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-surface-2 hover:text-ink'
          }
        >
          <BookOpen size={12} />
          {t('clip.page', { n: page })}
        </button>
      </div>
    </div>
  )
}
